// src/modules/app-views/dataSearch/pages/Search/components/DataFreshnessAlert.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Alert, Button, Flex, Typography } from 'antd';
import { SyncOutlined, InfoCircleOutlined, WarningOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pt-br';
import translateX from 'utils/translateX';

// Configure dayjs
dayjs.extend(relativeTime);
dayjs.locale('pt-br');

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
/**
 * DataFreshnessAlert - Shows data age warning based on months since last search
 * Displays an alert indicating the freshness/age of searched data.
 * - < 6 months: Info (blue) - "Dados atualizados recentemente"
 * - 6-12 months: Warning (yellow) - "Dados desatualizados"
 * - > 12 months: Error (red) - "Dados muito antigos"
 *
 * @param {Object} props
 * @param {number|null} props.dataAgeMonths - Age of data in months (null if first search)
 * @param {string|Date} [props.searchedAt] - Date when data was searched (for dayjs.fromNow())
 * @param {Function} [props.onRefresh] - Callback to refresh/re-search the data
 * @param {boolean} [props.showRefreshButton=true] - Whether to show refresh button for stale data
 * @param {Object} [props.style] - Additional styles
 */
const DataFreshnessAlert = ({
  dataAgeMonths,
  searchedAt,
  onRefresh,
  showRefreshButton = true,
  style,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // Don't show alert if data age is unknown (first search)
  if (dataAgeMonths === null || dataAgeMonths === undefined) {
    return null;
  }

  // Determine freshness status and alert configuration
  const alertConfig = getAlertConfig(dataAgeMonths);
  const { alertType, alertIcon } = alertConfig;

  // Format relative time using dayjs
  // If searchedAt is provided, use it; otherwise estimate from dataAgeMonths
  const estimatedSearchDate = searchedAt || dayjs().subtract(dataAgeMonths, 'month').toDate();
  const relativeTimeText = dayjs(estimatedSearchDate).fromNow();

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  // Build action button for stale/old data
  const action = (showRefreshButton && dataAgeMonths >= 6 && onRefresh) ? (
    <Button
      size="small"
      type="primary"
      icon={<SyncOutlined />}
      onClick={onRefresh}
    >
      {translateX('refresh_data')}
    </Button>
  ) : null;

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Alert
      type={alertType}
      showIcon
      icon={alertIcon}
      title={
        <Flex align="center" gap={4}>
          <Text style={{ fontSize: 13 }}>
            {translateX('data_searched')} <strong>{relativeTimeText}</strong>
          </Text>
        </Flex>
      }
      action={action}
      style={{
        marginBottom: 16,
        padding: '8px 12px',
        ...style,
      }}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default DataFreshnessAlert;

// *******************************************************************************
// HELPERS
// *******************************************************************************
/**
 * Get alert configuration based on data age in months
 */
function getAlertConfig(dataAgeMonths) {
  if (dataAgeMonths < 6) {
    // Fresh data: < 6 months
    return {
      alertType: 'info',
      alertIcon: <InfoCircleOutlined />,
    };
  } else if (dataAgeMonths < 12) {
    // Stale data: 6-12 months
    return {
      alertType: 'warning',
      alertIcon: <WarningOutlined />,
    };
  } else {
    // Old data: > 12 months
    return {
      alertType: 'error',
      alertIcon: <ClockCircleOutlined />,
    };
  }
}
